import { useCallback, useEffect, useMemo, useState } from "react";

const DESKTOP_WIDTH = 620;
const DESKTOP_PADDING = 80;
const MOBILE_BREAKPOINT = 640;
const MOBILE_MARGIN = 24;
const MOBILE_MAX_WIDTH = 420;
const MIN_CANVAS_WIDTH = 80;

interface CanvasSize {
  readonly width: number;
  readonly height: number;
  readonly padding: number;
  readonly isMobile: boolean;
}

function computeBoardAspectRatio(cols: number, rows: number): number {
  const widthCells = Math.max(1, cols - 1);
  const heightCells = Math.max(1, rows - 1);
  return heightCells / widthCells;
}

function computeCanvasSize(
  windowWidth: number,
  windowHeight: number,
  boardCols: number,
  boardRows: number,
): CanvasSize {
  const boardRatio = computeBoardAspectRatio(boardCols, boardRows);
  const isLandscape = windowWidth > windowHeight;
  const isMobile = windowWidth <= MOBILE_BREAKPOINT;

  if (!isMobile) {
    // Desktop: constrain by both width and height
    const maxWidth = isLandscape
      ? Math.min(DESKTOP_WIDTH, Math.round(windowWidth * 0.45))
      : DESKTOP_WIDTH;
    const maxHeightPx = windowHeight - 64;
    const widthFromHeight = Math.round(maxHeightPx / boardRatio);
    const width = Math.min(maxWidth, widthFromHeight);
    return {
      width,
      height: Math.round(width * boardRatio),
      padding: DESKTOP_PADDING,
      isMobile: false,
    };
  }

  if (isLandscape) {
    // Mobile landscape: canvas sits in the left half
    const maxWidth = Math.round(windowWidth * 0.45);
    const maxHeightPx = windowHeight - 16;
    const widthFromHeight = Math.round(maxHeightPx / boardRatio);
    const clampedWidth = Math.max(
      Math.min(maxWidth, widthFromHeight, MOBILE_MAX_WIDTH),
      MIN_CANVAS_WIDTH,
    );
    return {
      width: clampedWidth,
      height: Math.round(clampedWidth * boardRatio),
      padding: Math.round(clampedWidth * 0.13),
      isMobile: true,
    };
  }

  // Mobile portrait: canvas sits in the top half
  const maxWidth = windowWidth - MOBILE_MARGIN;
  const maxHeightPx = Math.round(windowHeight * 0.45);
  const widthFromHeight = Math.round(maxHeightPx / boardRatio);
  const clampedWidth = Math.max(
    Math.min(maxWidth, widthFromHeight, MOBILE_MAX_WIDTH),
    MIN_CANVAS_WIDTH,
  );
  return {
    width: clampedWidth,
    height: Math.round(clampedWidth * boardRatio),
    padding: Math.round(clampedWidth * 0.13),
    isMobile: true,
  };
}

export function useCanvasSize(
  boardCols: number,
  boardRows: number,
): CanvasSize {
  const dimensions = useMemo(
    () => ({ boardCols, boardRows }),
    [boardCols, boardRows],
  );

  const [size, setSize] = useState<CanvasSize>(() =>
    computeCanvasSize(
      window.innerWidth,
      window.innerHeight,
      dimensions.boardCols,
      dimensions.boardRows,
    ),
  );

  const handleResize = useCallback(() => {
    setSize(
      computeCanvasSize(
        window.innerWidth,
        window.innerHeight,
        dimensions.boardCols,
        dimensions.boardRows,
      ),
    );
  }, [dimensions]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const debouncedResize = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(handleResize, 150);
    };

    window.addEventListener("resize", debouncedResize);

    return () => {
      window.removeEventListener("resize", debouncedResize);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [handleResize]);

  useEffect(() => {
    handleResize();
  }, [handleResize]);

  return size;
}
